import { type ValidationRule } from '../../types';
import { ActionRequiredError } from '../../util/errors';
import { assertNotExamplePlaceholder } from '../../util/examplePlaceholder';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';

type CNCOperateWorkholdingArgs = {
  argumentKind: 'CNCOperateWorkholding';
  deviceID?: string;
  workholdingID?: number;
  actionID?: string;
};

class CNCOperateWorkholdingRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.CNCOperateWorkholding', 'CNCOperateWorkholding');
  }

  protected validate({ args, ctx }: CanonicalValidateArgs): void {
    const a = args as unknown as CNCOperateWorkholdingArgs;
    this.checkDevice(a, ctx);
    this.checkWorkholding(a);
    this.checkAction(a);
  }

  /**
   * Ensure a CNC machine device is selected and exists in equipment.
   *
   * @throws {ActionRequiredError}
   */
  private checkDevice(
    args: CNCOperateWorkholdingArgs,
    ctx: CanonicalValidateArgs['ctx'],
  ): void {
    if (!args.deviceID) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Machine not selected',
        fieldId: 'deviceID',
      });
    }

    // Before the exists-check: a copied example placeholder would otherwise
    // be reported as equipment that was removed from this robot.
    assertNotExamplePlaceholder(args.deviceID, 'deviceID');

    const equipment = ctx.equipment ?? [];
    const device = equipment.find((e) => e.id === args.deviceID);

    if (device == null || device.config?.kind !== 'CNCMachine') {
      throw new ActionRequiredError({
        kind: 'missingEquipment',
        message: 'Machine not available',
        fieldId: 'deviceID',
      });
    }
  }

  /**
   * Ensure a workholding is selected.
   *
   * @throws {ActionRequiredError}
   */
  private checkWorkholding(args: CNCOperateWorkholdingArgs): void {
    if (args.workholdingID == null) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Workholding not selected',
        fieldId: 'workholdingID',
      });
    }
  }

  /**
   * Ensure an action is selected.
   *
   * @throws {ActionRequiredError}
   */
  private checkAction(args: CNCOperateWorkholdingArgs): void {
    if (!args.actionID) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Action is required',
        fieldId: 'actionID',
      });
    }
  }
}

export const CNCOperateWorkholdingValidation: ValidationRule =
  new CNCOperateWorkholdingRule().toValidationRule();
